import { cn } from '@/lib/utils/cn';

/**
 * SupersedeChainViz — T-0102 / E06-F02 §1.
 *
 * Hand-rolled SVG timeline of the SOW supersede chain. One node per SOW
 * version, edges connecting them with `superseded_by_sow_id`. Each node
 * shows the version label + effective date; the active version (current
 * head) gets the amber accent fill, prior versions get the grey/ink-300
 * fill. Click a node → selectNode(sowId) so the parent screen can swap the
 * detail view to that version's revision list.
 *
 * D3 was considered and rejected (ticket §6 Risks-and-unknowns) — the chain
 * is at most a handful of nodes (a typical engagement has 0-3 change
 * orders per the prototype) and a horizontal layout is trivial with raw
 * SVG primitives. Adding D3 to the bundle would pay 35kb gzipped for a
 * tiny benefit.
 *
 * Layout:
 *   - Horizontal axis, oldest version leftmost.
 *   - Each node is a 36px circle with the version label in mono inside.
 *   - Below each node: the effective date.
 *   - Above the active node: a small "current" chip.
 *   - Edges are 2px ink-300 lines between node centers.
 *
 * The component is responsive — it scales the viewBox to the container
 * width, so the chain stretches to fill the available horizontal space
 * regardless of node count.
 */

export interface SupersedeChainNode {
  /** SOW row id — used as the click target. */
  sow_id: string;
  /** Display label, e.g. "v0", "v1", "v2". */
  versionLabel: string;
  /** Effective date (YYYY-MM-DD) — shown beneath the node. */
  effectiveDate: string;
  /** Backend status string ('current' | 'superseded'). */
  status: string;
  /** Optional change-order id label rendered above the node — pulled
   *  through to identify which change order introduced this version. */
  changeOrderLabel?: string | null;
}

export interface SupersedeChainVizProps {
  /** Nodes in chronological order (oldest first). */
  nodes: ReadonlyArray<SupersedeChainNode>;
  /** Which node is currently selected (the detail view target). */
  selectedSowId?: string | null;
  /** Click handler — fired when a node is activated. */
  onSelect?: (sowId: string) => void;
  className?: string;
}

const NODE_RADIUS = 18;
const NODE_VERTICAL_CENTER = 56;
const SIDE_PAD = 32;
const NODE_SPACING = 140;

export function SupersedeChainViz({
  nodes,
  selectedSowId,
  onSelect,
  className,
}: SupersedeChainVizProps) {
  if (nodes.length === 0) {
    return (
      <div
        className={cn(
          'rounded-card border border-dashed border-ink-200 bg-paper-50 p-6 text-center',
          className,
        )}
        role="status"
      >
        <p className="text-body-sm text-ink-500">
          No SOW versions yet. Register a baseline SOW first.
        </p>
      </div>
    );
  }

  const width = Math.max(
    SIDE_PAD * 2 + NODE_RADIUS * 2,
    SIDE_PAD * 2 + (nodes.length - 1) * NODE_SPACING + NODE_RADIUS * 2,
  );
  const height = 124;

  const nodePositions = nodes.map((_, i) => ({
    cx: SIDE_PAD + NODE_RADIUS + i * NODE_SPACING,
    cy: NODE_VERTICAL_CENTER,
  }));

  return (
    <div className={cn('rounded-card border border-ink-100 bg-paper-0 p-4', className)}>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          style={{ minWidth: width, maxWidth: '100%', display: 'block' }}
          role="img"
          aria-label="SOW supersede chain timeline"
        >
          {/* Edges — drawn first so nodes render on top. */}
          {nodes.slice(0, -1).map((_, i) => {
            const from = nodePositions[i]!;
            const to = nodePositions[i + 1]!;
            return (
              <line
                key={`edge-${i}`}
                x1={from.cx + NODE_RADIUS}
                y1={from.cy}
                x2={to.cx - NODE_RADIUS}
                y2={to.cy}
                stroke="var(--ink-300)"
                strokeWidth="2"
              />
            );
          })}

          {/* Nodes — circles + version labels inside; effective date underneath;
              CO label above when present; "current" chip when status='current'. */}
          {nodes.map((node, i) => {
            const pos = nodePositions[i]!;
            const isSelected = selectedSowId === node.sow_id;
            const isCurrent = node.status === 'current';
            const fillColor = isSelected
              ? 'var(--accent-amber-600)'
              : isCurrent
                ? 'var(--accent-amber-500)'
                : 'var(--ink-300)';
            const textColor = '#FFFFFF';
            return (
              <g
                key={node.sow_id}
                onClick={() => onSelect?.(node.sow_id)}
                tabIndex={onSelect ? 0 : -1}
                role={onSelect ? 'button' : undefined}
                aria-label={`SOW ${node.versionLabel}, effective ${node.effectiveDate}`}
                onKeyDown={(e) => {
                  if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onSelect(node.sow_id);
                  }
                }}
                style={{ cursor: onSelect ? 'pointer' : 'default' }}
              >
                {isCurrent ? (
                  <g>
                    <rect
                      x={pos.cx - 28}
                      y={pos.cy - NODE_RADIUS - 22}
                      width="56"
                      height="16"
                      rx="8"
                      fill="var(--posture-healthy-tint)"
                    />
                    <text
                      x={pos.cx}
                      y={pos.cy - NODE_RADIUS - 11}
                      textAnchor="middle"
                      fontSize="10"
                      fill="var(--posture-healthy)"
                      fontFamily="var(--font-ui)"
                      fontWeight="600"
                    >
                      current
                    </text>
                  </g>
                ) : null}
                {node.changeOrderLabel && !isCurrent ? (
                  <text
                    x={pos.cx}
                    y={pos.cy - NODE_RADIUS - 8}
                    textAnchor="middle"
                    fontSize="10"
                    fill="var(--ink-500)"
                    fontFamily="var(--font-mono)"
                  >
                    {node.changeOrderLabel}
                  </text>
                ) : null}
                <circle
                  cx={pos.cx}
                  cy={pos.cy}
                  r={NODE_RADIUS}
                  fill={fillColor}
                  stroke={isSelected ? 'var(--accent-amber-700)' : 'transparent'}
                  strokeWidth="2"
                />
                <text
                  x={pos.cx}
                  y={pos.cy + 4}
                  textAnchor="middle"
                  fontSize="12"
                  fill={textColor}
                  fontFamily="var(--font-mono)"
                  fontWeight="600"
                >
                  {node.versionLabel}
                </text>
                <text
                  x={pos.cx}
                  y={pos.cy + NODE_RADIUS + 16}
                  textAnchor="middle"
                  fontSize="11"
                  fill="var(--ink-700)"
                  fontFamily="var(--font-mono)"
                >
                  {node.effectiveDate}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
